import React, { forwardRef, useMemo } from 'react';
import cn from 'classnames';
import { Divider } from 'apps/sigma2-ui/src/components/ui/Divider';

type Size =
  | 'xs'
  | 'sm'
  | '' // this is analog to when you are using the between size of small and medium in tailwind
  | 'md'
  | 'lg'
  | 'xl'
  | '2xl'
  | '3xl'
  | 'justify-between';
type Direction = 'horizontal' | 'vertical';

const sizeMap: Record<Size, string> = {
  xs: 'gap-1',
  sm: 'gap-2',
  '': 'gap-3',
  md: 'gap-4',
  lg: 'gap-6',
  xl: 'gap-8',
  '2xl': 'gap-10',
  '3xl': 'gap-12',
  'justify-between': 'justify-between',
};

const isSize = (size: string): size is Size => {
  return size in sizeMap;
};

const getSizeClassName = (size: Size | string) => {
  return isSize(size) ? sizeMap[size] : size;
};

const directionMap: Record<Direction, string> = {
  horizontal: 'flex-row',
  vertical: 'flex-col',
};

const getOppositeDirection = (direction: Direction) => {
  return direction === 'horizontal' ? 'vertical' : 'horizontal';
};

const ChildrenWithDividers: React.FC<{
  children: React.ReactNode;
  size: SpacingProps['size'];
  direction: SpacingProps['direction'];
  className?: string;
}> = ({ children, size, direction }) => {
  if (!children) {
    return null;
  }
  const nonEmptyChildren = React.Children.toArray(children).filter(
    (child) => child !== null && child !== undefined,
  );

  const nonEmptyChildrenLength = React.Children.count(nonEmptyChildren);

  return (
    <>
      {React.Children.map(nonEmptyChildren, (child, index) => {
        const isLastChild = index === nonEmptyChildrenLength - 1;

        if (isLastChild) {
          return <div>{child}</div>;
        }

        return (
          <div
            className={cn(
              'flex',
              getSizeClassName(size),
              directionMap[direction],
            )}
          >
            {child}

            <Divider direction={getOppositeDirection(direction)} />
          </div>
        );
      })}
    </>
  );
};

export interface SpacingProps {
  children: React.ReactNode;
  /** The size of the spacing
   * Can be custom size, e.g. 'gap-10' or an enum value, e.g. 'md'
   * */
  size: Size | string;
  direction: Direction;
  divider?: boolean;
  className?: string;
  as?: 'div' | 'span';
}

/**
 * Allows you to accomplish a row or column with consistent spacing in Tailwind
 *
 * size:
 *   - none: 0px: gap-0
 *   - xs: 4px  : gap-1
 *   - sm: 8px  : gap-2
 *   - '': 12px : gap-3
 *   - md: 16px : gap-4
 *   - lg: 24px : gap-6
 *   - xl: 32px : gap-8
 *   - 2xl: 40px : gap-10
 *   - 3xl: 48px : gap-12
 *
 * direction: horizontal, vertical
 * */
export const Spacing = forwardRef(function Spacing(
  { children, className, size, direction, as, divider = false }: SpacingProps,
  ref: React.Ref<HTMLDivElement>,
) {
  const childrenAndDividers = useMemo(() => {
    return divider ? (
      <ChildrenWithDividers
        className={className}
        size={size}
        direction={direction}
      >
        {children}
      </ChildrenWithDividers>
    ) : (
      children
    );
  }, [children, className, size, direction, divider]);

  const fullClassName = cn(
    'flex',
    getSizeClassName(size),
    directionMap[direction],
    className,
  );

  const asValue = as ?? 'div';

  if (asValue === 'span') {
    return (
      <span ref={ref} className={fullClassName}>
        {childrenAndDividers}
      </span>
    );
  }

  return (
    <div ref={ref} className={fullClassName}>
      {childrenAndDividers}
    </div>
  );
});
